import { MeasurementLinkRecord } from "common/records/MeasurementLink";
import {
  IMeasurementRecord,
  MeasurementRecord,
  defaultMeasurementProps,
  lineMeasurementUpdate,
  measurementFromJSON,
} from "common/records/Measurement";
import { RootActions, RootState } from "common/reducers";
import {
  APPEND_ELEVATION_ID,
  ADD_WIZARD_OPENINGS,
  APPEND_WINDOW_ID,
  APPEND_DOOR_ID,
  FETCH_MEASUREMENT,
  MeasurementActions,
  RECEIVE_MEASUREMENT,
  REMOVE_ELEVATION_ID,
  RESET_MEASUREMENT,
  SET_MEASUREMENT_EDITING,
  SET_MEASUREMENT_LOADED,
  UPDATE_LINE_MEASUREMENTS,
  UPDATE_MEASUREMENT,
  UPDATE_ROOF_WASTE,
  UPDATE_SIDING_WASTE,
  UPDATE_SLOPED_ROOF_WASTE,
  UPDATE_RAPID_AREA,
  UPDATE_LEAFLET,
  UPDATE_LEAFLET_ZOOM,
  CREATE_FROM_ADD_MEASUREMENT_MODAL,
} from "common/reducers/measurement.actions";
import { List, Map, Record } from "immutable";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { reducer as rsfFormReducer } from "common/reducers/rsfForm.reducer";
import { calculateRoofAreaFromPitch, calculateRoofPitch, calculateRoofPitchAngle } from "common/helpers/Measurement";
import { windowReducer } from "common/reducers/window.reducer";
import { WindowActions } from "common/reducers/window.actions";
import { doorReducer } from "common/reducers/door.reducer";
import { DoorActions } from "common/reducers/door.actions";
import { reducer as roofFaceReducer } from "common/reducers/roofFace.reducer";
import { RoofFaceActions } from "common/reducers/roofFace.actions";
import { reducer as wallFacadeReducer } from "common/reducers/wallFacade.reducer";
import { WallFacadeActions } from "common/reducers/wallFacade.actions";
import { round } from "common/helpers/Math";
import { calculateOpeningArea, calculateOpeningUI, IOpeningData, OpeningRecord } from "common/records/Opening";
import { currentMeasurement } from "common/selectors/measurement.common.selectors";
import { form } from "common/selectors/rsfForm.selectors";
import { roomReducer } from "common/reducers/room.reducer";
import { RoomActions } from "common/reducers/room.actions";
import { elevationReducer } from "common/reducers/elevation.reducer";
import { ElevationActions } from "common/reducers/elevation.actions";

export const MeasurementStateRecord = Record({
  byId: Map<number, MeasurementLinkRecord>(),
  lastSavedById: Map<number, MeasurementRecord>(),
  editing: false,
});

export const initialState = MeasurementStateRecord();

export type MeasurementState = typeof initialState;

export const model = "measurements";
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  let measurement: MeasurementRecord;
  switch (action.type) {
    case FETCH_MEASUREMENT:
      return fetch(state, model, measurementFromJSON({ id: action.payload.measurementId }));

    case RECEIVE_MEASUREMENT:
      const payloadMeasurement = action.payload.measurement;
      measurement = measurementFromJSON({ ...payloadMeasurement });

      payloadMeasurement.roof_faces?.forEach((roofFace) => {
        state = roofFaceReducer(state, RoofFaceActions.receiveRoofFace(roofFace));
      });

      payloadMeasurement?.wall_facades?.forEach((wallFacade) => {
        state = wallFacadeReducer(state, WallFacadeActions.receiveWallFacade(wallFacade));
      });

      payloadMeasurement?.windows?.forEach((window) => {
        state = windowReducer(state, WindowActions.receiveWindow(window));
      });

      payloadMeasurement?.doors?.forEach((door) => {
        state = doorReducer(state, DoorActions.receiveDoor(door));
      });

      payloadMeasurement?.rooms?.forEach((room) => {
        state = roomReducer(state, RoomActions.receiveRoom(room));
      });

      payloadMeasurement?.elevations?.forEach((elevation) => {
        state = elevationReducer(state, ElevationActions.receiveElevation(elevation));
      });

      // Loads/saves that don't include elevations (e.g. the openings save path — `cleanProps`
      // strips them from the PATCH, and callers may omit the include) must not wipe the known
      // elevation links: keep the prior elevation_ids unless the payload explicitly carries an
      // elevations array.
      if (!payloadMeasurement.elevations) {
        const priorElevationIds = state.getIn([model, "byId", payloadMeasurement.id, "elevation_ids"]) as
          | List<number>
          | undefined;
        if (priorElevationIds && priorElevationIds.size > 0) {
          measurement = measurement.set("elevation_ids", priorElevationIds);
        }
      }

      return receive(state, model, measurement);

    case SET_MEASUREMENT_LOADED:
      return loaded(state, model, action.payload.measurementId);

    case SET_MEASUREMENT_EDITING:
      return state.setIn([model, "editing"], action.payload.editing);

    case RESET_MEASUREMENT: {
      const measurement = currentMeasurement(state);
      if (measurement) {
        measurement.get("window_ids").forEach((windowId) => {
          state = windowReducer(state, WindowActions.resetWindow(windowId));
        });
        measurement.get("door_ids").forEach((doorId) => {
          state = doorReducer(state, DoorActions.resetDoor(doorId));
        });
        measurement.get("room_ids").forEach((roomId) => {
          state = roomReducer(state, RoomActions.resetRoom(roomId));
        });
      }
      return reset(state, model, measurement?.get("id"));
    }

    case UPDATE_MEASUREMENT:
      const { rootPath, name } = action.payload.event;
      let { value } = action.payload.event;

      const nameArray = name.split(".");
      const lastName = nameArray[nameArray.length - 1];
      if (typeof defaultMeasurementProps[lastName as keyof IMeasurementRecord] === "number") {
        value = round(+value || 0, 2);
      }

      state = state.setIn(rootPath.concat(name.split(".")), value);

      if (lastName === "roof_total_area") {
        state = reducer(state, MeasurementActions.updateRoofWaste());
      }

      if (lastName === "siding_total_area") {
        state = reducer(state, MeasurementActions.updateSidingWaste());
      }

      if (lastName === "roof_sloped_area") {
        state = reducer(state, MeasurementActions.updateSlopedRoofWaste());
      }

      if (lineMeasurementUpdate.includes(lastName)) {
        state = reducer(state, MeasurementActions.updateLineMeasurements());
      }

      return state;

    case UPDATE_ROOF_WASTE:
      measurement = currentMeasurement(state);
      const percents = [5, 10, 12, 15, 20];
      percents.forEach((percent) => {
        const key = `roof_${percent}pw_total_area` as keyof IMeasurementRecord;
        const value = round(measurement.get("roof_total_area") * (1 + percent / 100), 2);
        measurement = measurement.set(key, value);
      });
      return state.setIn([model, "byId", measurement.get("id")], measurement);

    case UPDATE_SLOPED_ROOF_WASTE:
      measurement = currentMeasurement(state);
      const slopedPercents = [10];
      slopedPercents.forEach((percent) => {
        const key = `roof_${percent}pw_sloped_area` as keyof IMeasurementRecord;
        const value = round(measurement.get("roof_sloped_area") * (1 + percent / 100), 2);
        measurement = measurement.set(key, value);
      });
      return state.setIn([model, "byId", measurement.get("id")], measurement);

    case UPDATE_LINE_MEASUREMENTS:
      measurement = currentMeasurement(state);
      measurement = measurement
        // perimeter (rake + eave)
        .set("roof_perimeter", round(measurement.get("roof_eave") + measurement.get("roof_rake"), 2))
        // ridge & hip (ridge + hip)
        .set("roof_ridge_hip", round(measurement.get("roof_ridge") + measurement.get("roof_hip"), 2))
        // total flashing (step + headwall)
        .set(
          "roof_total_flashing",
          round(measurement.get("roof_step_flashing") + measurement.get("roof_headwall_flashing"), 2),
        )
        // valley & eave (valley + eave)
        .set("roof_valley_eave", round(measurement.get("roof_valley") + measurement.get("roof_eave"), 2))
        // valley, eave, rake (valley + eave + rake)
        .set(
          "roof_valley_eave_rake",
          round(measurement.get("roof_valley") + measurement.get("roof_eave") + measurement.get("roof_rake"), 2),
        );

      return state.setIn([model, "byId", measurement.get("id")], measurement);

    case UPDATE_SIDING_WASTE:
      measurement = currentMeasurement(state);
      const sidingPercents = [5, 10, 15, 18];
      sidingPercents.forEach((percent) => {
        const key = `siding_${percent}pw_total_area` as keyof IMeasurementRecord;
        const value = round(measurement.get("siding_total_area") * (1 + percent / 100), 2);
        measurement = measurement.set(key, value);
      });
      return state.setIn([model, "byId", measurement.get("id")], measurement);

    case UPDATE_RAPID_AREA: {
      const { event } = action.payload;

      state = rsfFormReducer(state, RsfFormActions.update(event));

      const rapidAreaForm = (state.getIn(event.rootPath) as any).toJS();
      let { pitch, degrees } = rapidAreaForm;
      const { area } = rapidAreaForm;

      const { name } = event;

      if (name === "pitch") {
        degrees = calculateRoofPitchAngle(pitch);
        state = rsfFormReducer(
          state,
          RsfFormActions.update({ rootPath: event.rootPath, name: "degrees", value: degrees }),
        );
      }

      if (name === "degrees") {
        pitch = calculateRoofPitch(degrees);
        state = rsfFormReducer(state, RsfFormActions.update({ rootPath: event.rootPath, name: "pitch", value: pitch }));
      }

      if (area <= 0 || pitch <= 0) {
        return state.setIn(["rsfForms", "byForm", "rapidArea", "calculatedArea"], area);
      }

      const calculatedArea = calculateRoofAreaFromPitch(area, pitch);

      return state.setIn(["rsfForms", "byForm", "rapidArea", "calculatedArea"], calculatedArea);
    }

    case UPDATE_LEAFLET: {
      const { event } = action.payload;
      measurement = currentMeasurement(state);
      measurement = measurement.setIn(["geojson", "leaflet"], List(event));

      return state.setIn([model, "byId", measurement.get("id")], measurement);
    }

    case UPDATE_LEAFLET_ZOOM: {
      const { zoom } = action.payload;
      measurement = currentMeasurement(state);
      measurement = measurement.setIn(["geojson", "leafletZoom"], zoom);

      return state.setIn([model, "byId", measurement.get("id")], measurement);
    }
    case APPEND_ELEVATION_ID: {
      // Keeps `currentMeasurement.elevation_ids` in sync when the Window-inator wizard introduces
      // an elevation (placeholder id from Step 1, or real id from the createElevation thunk's
      // success path). Without this, `currentMeasurementElevations` — which derives the overview
      // list from elevation_ids — would not surface the new elevation until a full measurement
      // refetch.
      const currentForAppend = currentMeasurement(state);
      if (!currentForAppend) return state;
      const ids = currentForAppend.get("elevation_ids");
      if (ids.includes(action.payload.elevationId)) return state;
      return state.setIn(
        [model, "byId", currentForAppend.get("id")],
        currentForAppend.set("elevation_ids", ids.push(action.payload.elevationId)),
      );
    }

    case REMOVE_ELEVATION_ID: {
      // Symmetric counterpart to APPEND_ELEVATION_ID — used when the wizard's Cancel on a new
      // (placeholder) elevation drops it without persisting.
      const currentForRemove = currentMeasurement(state);
      if (!currentForRemove) return state;
      const ids = currentForRemove.get("elevation_ids");
      const idx = ids.indexOf(action.payload.elevationId);
      if (idx < 0) return state;
      return state.setIn(
        [model, "byId", currentForRemove.get("id")],
        currentForRemove.set("elevation_ids", ids.delete(idx)),
      );
    }

    case APPEND_WINDOW_ID: {
      // Keeps `currentMeasurement.window_ids` in sync when the Window-inator save flow creates a
      // new window from an annotation. `currentMeasurementWindows` (and therefore the hydrated
      // measurement `updateMeasurement` serializes) derives from `window_ids`, so a freshly
      // `receiveWindow`-ed record is invisible until its id is appended here. Dedupe-safe.
      const currentForWindow = currentMeasurement(state);
      if (!currentForWindow) return state;
      const windowIds = currentForWindow.get("window_ids");
      if (windowIds.includes(action.payload.windowId)) return state;
      return state.setIn(
        [model, "byId", currentForWindow.get("id")],
        currentForWindow.set("window_ids", windowIds.push(action.payload.windowId)),
      );
    }

    case APPEND_DOOR_ID: {
      // Door counterpart to APPEND_WINDOW_ID.
      const currentForDoor = currentMeasurement(state);
      if (!currentForDoor) return state;
      const doorIds = currentForDoor.get("door_ids");
      if (doorIds.includes(action.payload.doorId)) return state;
      return state.setIn(
        [model, "byId", currentForDoor.get("id")],
        currentForDoor.set("door_ids", doorIds.push(action.payload.doorId)),
      );
    }

    case CREATE_FROM_ADD_MEASUREMENT_MODAL: {
      const { type } = action.payload;
      const formData = form(state, { name: "addMeasurement" });
      let id = -1;
      const baseName = formData?.get("name");
      const quantity = formData?.get("quantity");
      let measurement = currentMeasurement(state);
      // Drop stale negative-id drafts left by a prior failed submit before re-creating.
      const draftKinds = [
        { idsKey: "window_ids", byIdKey: "windows" },
        { idsKey: "door_ids", byIdKey: "doors" },
        { idsKey: "room_ids", byIdKey: "rooms" },
      ] as const;
      draftKinds.forEach(({ idsKey, byIdKey }) => {
        const staleIds = measurement.get(idsKey).filter((openingId) => openingId < 0);
        if (staleIds.isEmpty()) return;
        measurement = measurement.set(
          idsKey,
          measurement.get(idsKey).filter((openingId) => openingId >= 0),
        );
        staleIds.forEach((staleId) => {
          state = state.removeIn([byIdKey, "byId", staleId]);
        });
      });
      state = state.setIn([model, "byId", measurement.get("id")], measurement);
      // Dedupe local ids so a re-submit of the same draft doesn't append it twice (matches APPEND_*_ID).
      const pushId = (idsKey: "window_ids" | "door_ids" | "room_ids", localId: number) => {
        const ids = measurement.get(idsKey);
        if (ids.includes(localId)) return;
        measurement = measurement.set(idsKey, ids.push(localId));
      };
      switch (type) {
        case "door":
          for (let i = 0; i < quantity; i++) {
            let name = `${baseName}-${i + 1}`;
            if (quantity === 1) {
              name = baseName;
            }
            pushId("door_ids", id);
            state = state.setIn([model, "byId", measurement.get("id")], measurement);
            state = doorReducer(state, DoorActions.receiveDoor({ ...formData?.toJS(), id, name }));
            id -= 1;
          }
          break;
        case "window":
          for (let i = 0; i < quantity; i++) {
            let name = `${baseName}-${i + 1}`;
            if (quantity === 1) {
              name = baseName;
            }
            pushId("window_ids", id);
            state = state.setIn([model, "byId", measurement.get("id")], measurement);
            state = windowReducer(state, WindowActions.receiveWindow({ ...formData?.toJS(), id, name }));
            id -= 1;
          }
          break;
        case "room":
          for (let i = 0; i < quantity; i++) {
            let name = `${baseName}-${i + 1}`;
            if (quantity === 1) {
              name = baseName;
            }
            pushId("room_ids", id);
            state = state.setIn([model, "byId", measurement.get("id")], measurement);
            state = roomReducer(state, RoomActions.receiveRoom({ ...formData?.toJS(), id, name }));
            id -= 1;
          }
          break;
        default:
          break;
      }
      return state;
    }

    case ADD_WIZARD_OPENINGS: {
      // Ports rsf-app `Opening.addOpenings` for the Openings Wizard Step 3. An opening whose id
      // already belongs to the measurement (persisted positive id OR unsaved negative draft id) is
      // updated in place; a genuinely new opening (no/zero id, or an id not in the list) is created
      // — quantity > 1 fans out into numbered clones ("Name-1", "-2", …), skipping any name already
      // taken by an opening of the same kind, matching rsf-app's collision-skip loop.
      const { kind, opening, quantity } = action.payload;
      let measurement = currentMeasurement(state);
      if (!measurement) return state;

      const idsKey = `${kind}_ids` as "window_ids" | "door_ids";
      const openingReducer = kind === "window" ? windowReducer : doorReducer;
      // Draft receives (byId only, no lastSavedById) so a wizard Cancel/reset drops new openings and
      // reverts in-place edits — see receiveDraft. In-place edits merge over the existing record,
      // preserving fields the 7-field wizard form doesn't touch (designator, ref_id, sort_order, …).
      const receiveOpening = kind === "window" ? WindowActions.receiveDraftWindow : DoorActions.receiveDraftDoor;

      const withComputed = (data: Partial<IOpeningData>): Partial<IOpeningData> => ({
        ...data,
        measurement_id: measurement.get("id"),
        area: calculateOpeningArea(data.height, data.width),
        ui: calculateOpeningUI(data.height, data.width),
      });

      // Update in place: the id already lives in the measurement's opening ids, so only the record
      // is rewritten. Membership — not sign — is the test, so an unsaved draft (negative id) started
      // from an existing opening updates in place rather than minting a duplicate.
      const existingIds = measurement.get(idsKey);
      if (opening.id && existingIds.includes(opening.id)) {
        state = openingReducer(state, receiveOpening(withComputed(opening)));
        return state;
      }

      // Local ids for new openings decrement from the most-negative existing id (or 0) — never a
      // list-size offset, which collides after a mid-list delete or a prior unsaved batch.
      let nextId = existingIds.reduce((min, id) => Math.min(min, id), 0) - 1;

      const existingNames = new Set(
        existingIds
          .map((id) => state.getIn([idsKey.replace("_ids", "s"), "byId", id]) as OpeningRecord | undefined)
          .filter((o): o is OpeningRecord => Boolean(o))
          .map((o) => o.get("name")),
      );

      const baseName = opening.name ?? "";
      const total = quantity && quantity > 1 ? quantity : 1;
      for (let i = 0; i < total; i++) {
        let name = baseName;
        if (total > 1) {
          let index = i + 1;
          while (existingNames.has(`${baseName}-${index}`)) {
            index += 1;
          }
          name = `${baseName}-${index}`;
          existingNames.add(name);
        }
        measurement = measurement.set(idsKey, measurement.get(idsKey).push(nextId));
        state = state.setIn([model, "byId", measurement.get("id")], measurement);
        state = openingReducer(state, receiveOpening(withComputed({ ...opening, id: nextId, name })));
        nextId -= 1;
      }
      return state;
    }

    default:
      return state;
  }
};
